import { useReducedMotion } from 'framer-motion'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { trackClickEvent } from 'src/analytics/gtm'
import { UserProfile } from 'src/types/user'

import { STAGE_HEIGHT, STAGE_WIDTH } from './IdeaCarousel/geometry'
import IdeaStage from './IdeaCarousel/IdeaStage'
import type { CarouselCardModel } from './IdeaCarousel/types'
import backarrow from '../../assets/icons/backarrow.svg'
import { useHighlightedIdeas } from '../../hooks/useHighlightedIdeas'
import type { IdeaPreview } from '../../model/IdeaPreview'

const AUTO_ADVANCE_MS = 6000

function mod(n: number, m: number) {
  return ((n % m) + m) % m
}

export interface IdeaCarouselProps {
  /** Called when the highlighted (main) card is clicked — route to the Idea Page here. */
  onOpenIdea?: (idea: CarouselCardModel) => void
  /** URL to navigate to the Ideas Bank page */
  ideasBankUrl?: string
  userData?: UserProfile
}

export default function IdeaCarousel({ onOpenIdea, userData }: IdeaCarouselProps) {
  const reduceMotion = useReducedMotion()
  const navigateUrl = useNavigate()
  const { data, isLoading, isError } = useHighlightedIdeas({ page: 1, pageSize: 10 })

  const ideasPreview: IdeaPreview[] = useMemo(() => data ?? [], [data])

  const pushClick = (payload: Parameters<typeof trackClickEvent>[0]): void => {
    trackClickEvent(payload)
  }

  function handleNavigate(): void {
    pushClick({
      module: 'Idea Carousel',
      module_title: 'Idea Carousel',
      click_text: 'Go to Ideas Bank',
      click_url: '/ideas-bank',
      click_element: 'ideas bank button',
    })
    navigateUrl('/ideas-bank')
  }
  const cards: CarouselCardModel[] = useMemo(() => {
    return (ideasPreview ?? [])
      .filter(x => !!x?.id)
      .map(idea => ({
        id: idea.id,
        title: idea.name,
        coverImageUrl: idea.coverImageUrl,
        brands: idea.brand ?? [],
      }))
  }, [ideasPreview])

  const [centerIndex, setCenterIndex] = useState(0)
  const autoAdvanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>()

  const count = cards.length
  // Navigate when center (main) card is clicked.
  const handleOpenIdea = (idea: CarouselCardModel) => {
    pushClick({
      module: 'Idea Carousel',
      module_title: 'Idea Carousel',
      click_text: idea.title ?? 'idea',
      click_url: `/ideas-bank/idea/${idea.id}`,
      click_element: 'card',
    })
    navigateUrl(`ideas-bank/idea/${idea.id}`)
  }

  const navigate = useCallback(
    (direction: 1 | -1) => {
      if (count < 2) return
      setCenterIndex(prev => mod(prev + direction, count))
    },
    [count],
  )

  useEffect(() => {
    if (isLoading) return
    if (!count) return

    if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current)
    autoAdvanceTimeoutRef.current = setTimeout(() => navigate(1), AUTO_ADVANCE_MS)
    return () => {
      if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current)
    }
  }, [centerIndex, navigate, isLoading, count])

  useEffect(() => {
    return () => {
      if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current)
    }
  }, [])

  const wrapperRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return

    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? STAGE_WIDTH
      setScale(Math.min(1, width / STAGE_WIDTH))
    })

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  if (isError) return null
  if (isLoading) return null
  if (count === 0) return null

  return (
    <div className="col-span-12 w-full mt-16">
      {/* Header row */}
      <div className="flex items-center justify-between mb-6 min-[1024px]:w-[976px] min-[1200px]:w-[1100px] min-[1440px]:w-[1296px] max-w-[768px]:w-full mx-auto">
        <h2 className="text-[#000] headline-m leading-tight">Get inspired today</h2>
        <button
          onClick={handleNavigate}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1A1A1A] text-white subtitle-s hover:bg-[#333333] transition-colors duration-100 whitespace-nowrap"
        >
          Go to Ideas Bank
          <img src={backarrow} alt="ideas arrow" width="20" height="20" className="rotate-180" />
        </button>
      </div>

      <div ref={wrapperRef} className="w-full flex justify-center">
        <div
          style={{
            width: STAGE_WIDTH * scale,
            height: (STAGE_HEIGHT + 80) * scale,
            maxWidth: '100%',
          }}
        >
          <div
            className="relative"
            style={{
              width: STAGE_WIDTH,
              transform: `scale(${scale})`,
              transformOrigin: 'top center',
            }}
          >
            <IdeaStage
              ideas={cards}
              centerIndex={centerIndex}
              userData={userData}
              onOpenIdea={onOpenIdea ?? handleOpenIdea}
              onNavigate={d => {
                if (reduceMotion) return
                navigate(d)
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
